import { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { getProjectsByAuctionIds } from '../services/projectService';

/**
 * Context that exposes { map, loading } where `map` is { auctionId: projectInfo | null }.
 * Wrapping a list in <ProjectsByAuctionProvider auctionIds={[...]}> lets every nested
 * <ProjectBadge auctionId={...} /> read its info from the batch instead of making its own API call.
 *
 * When the provider is absent (e.g. detail pages), ProjectBadge falls back to single fetch.
 */
const ProjectsByAuctionContext = createContext(null);

export const ProjectsByAuctionProvider = ({ auctionIds, enabled = true, children }) => {
  const [map, setMap] = useState({});
  const [loading, setLoading] = useState(false);

  // Stable key so we don't refetch on every array identity change.
  const key = useMemo(
    () => (auctionIds || []).filter(Boolean).slice().sort().join('|'),
    [auctionIds],
  );

  useEffect(() => {
    if (!enabled || !key) {
      setMap({});
      return;
    }

    let cancelled = false;
    setLoading(true);

    getProjectsByAuctionIds(key.split('|'))
      .then((data) => {
        if (!cancelled) setMap(data || {});
      })
      .catch((err) => {
        // Don't leave consumers in limbo if the batch fails — reset to empty map
        // so ProjectBadge renders the "no project" state instead of staying in loading forever.
        console.error('[ProjectsByAuctionProvider] Batch fetch failed:', err);
        if (!cancelled) setMap({});
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [key, enabled]);

  const value = useMemo(() => ({ map, loading }), [map, loading]);

  return (
    <ProjectsByAuctionContext.Provider value={value}>
      {children}
    </ProjectsByAuctionContext.Provider>
  );
};

/**
 * Consumer hook.
 * Returns:
 *   hasProvider — true if wrapped in a <ProjectsByAuctionProvider>, false otherwise.
 *                 When true, the caller MUST NOT fall back to individual fetch — the provider
 *                 owns this auction's data (even if still loading).
 *   hit         — true if the batch has resolved and contains this auctionId.
 *   info        — the project info (or null if no project contains this auction).
 *   loading     — true while the batch request is in flight.
 */
export const useProjectByAuction = (auctionId) => {
  const ctx = useContext(ProjectsByAuctionContext);
  if (!ctx) return { hasProvider: false, hit: false, loading: false };

  const has = !!auctionId && Object.prototype.hasOwnProperty.call(ctx.map, auctionId);
  return {
    hasProvider: true,
    hit: has,
    info: has ? ctx.map[auctionId] : undefined,
    loading: ctx.loading,
  };
};

export default ProjectsByAuctionContext;
